export type ErrorCategory =
  | 'ValidationError'
  | 'AuthenticationError'
  | 'AuthorizationError'
  | 'NotFound'
  | 'Conflict'
  | 'RateLimited'
  | 'Timeout'
  | 'NetworkError'
  | 'ServiceError'
  | 'UnknownError';

export interface FieldError {
  field: string;
  message: string;
}

export class AppError extends Error {
  constructor(
    public readonly category: ErrorCategory,
    message: string,
    public readonly statusCode?: number,
    public readonly fieldErrors?: FieldError[],
  ) {
    super(message);
    this.name = 'AppError';
  }
}

export function categorizeHttpError(status: number, body?: unknown): AppError {
  const msg = extractMessage(body);
  if (status === 400) return new AppError('ValidationError', msg || 'Validation failed', status, extractFieldErrors(body));
  if (status === 401) return new AppError('AuthenticationError', msg || 'Session expired. Please log in again.', status);
  if (status === 403) return new AppError('AuthorizationError', 'You do not have permission to perform this action.', status);
  if (status === 404) return new AppError('NotFound', msg || 'The requested resource was not found.', status);
  if (status === 409) return new AppError('Conflict', msg || 'This operation conflicts with existing data.', status);
  if (status === 429) return new AppError('RateLimited', 'Too many requests. Please wait and try again.', status);
  if (status >= 500) return new AppError('ServiceError', 'A server error occurred. Please try again later.', status);
  return new AppError('UnknownError', msg || 'An unexpected error occurred.', status);
}

function extractMessage(body: unknown): string {
  if (!body || typeof body !== 'object') return '';
  const b = body as Record<string, unknown>;
  const nested = b['error'] as Record<string, unknown> | null | undefined;
  return (
    (b['Resp_desc'] as string) ||
    (b['message'] as string) ||
    (nested?.['error'] as string) ||
    (nested?.['message'] as string) ||
    ''
  );
}

function extractFieldErrors(body: unknown): FieldError[] {
  if (!body || typeof body !== 'object') return [];
  const b = body as Record<string, unknown>;
  if (Array.isArray(b['errors'])) {
    return (b['errors'] as Array<{ field: string; message: string }>).map(e => ({
      field: e.field,
      message: e.message,
    }));
  }
  return [];
}
